import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Field, Input } from "@/components/kit";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";

const NEW = "__new";
const NONE = "__none";

export function SelectCreate({ label, endpoint, valueKey = "id", value, onChange, allowNone, noneLabel = "Sem categoria", placeholder = "Selecione", newLabel = "Nova categoria", createdMsg = "Categoria criada", testid, enabled = true }) {
  const qc = useQueryClient();
  const key = endpoint.replace("/", "");
  const { data: items = [] } = useQuery({ queryKey: [key], queryFn: () => api(endpoint), enabled });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => api(endpoint, { method: "POST", body: { name: name.trim() } }),
    onSuccess: async (d) => { await qc.invalidateQueries({ queryKey: [key] }); onChange(d[valueKey]); setCreating(false); setName(""); toast.success(createdMsg); },
    onError: (e) => toast.error(e.message),
  });
  const pick = (v) => (v === NEW ? setCreating(true) : onChange(v === NONE ? "" : v));
  const cancel = () => { setCreating(false); setName(""); };
  return (
    <Field label={label}>
      {creating ? (
        <div className="flex items-center gap-1.5">
          <Input autoFocus className="flex-1" placeholder={newLabel} value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) { e.preventDefault(); add.mutate(); } if (e.key === "Escape") { e.stopPropagation(); cancel(); } }} data-testid={`${testid}-new-input`} />
          <button type="button" className="icon-btn" disabled={!name.trim() || add.isPending} onClick={() => add.mutate()} data-testid={`${testid}-new-save`}><Check size={16} /></button>
          <button type="button" className="icon-btn" onClick={cancel} data-testid={`${testid}-new-cancel`}><X size={16} /></button>
        </div>
      ) : (
        <Select value={value || (allowNone ? NONE : undefined)} onValueChange={pick}>
          <SelectTrigger className="input h-auto" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
          <SelectContent className="bg-[var(--surface)] text-[var(--text)] border-[var(--border)] z-[60]">
            {allowNone && <SelectItem value={NONE} data-testid={`${testid}-none`}>{noneLabel}</SelectItem>}
            {items.map((i) => <SelectItem key={i.id} value={i[valueKey]} data-testid={`${testid}-${i.id}`}>{i.name}</SelectItem>)}
            <SelectSeparator />
            <SelectItem value={NEW} className="font-semibold text-[var(--brand)]" data-testid={`${testid}-new`}><span className="flex items-center gap-1.5"><Plus size={14} /> {newLabel}</span></SelectItem>
          </SelectContent>
        </Select>
      )}
    </Field>
  );
}
